/**
 * Auth Callback Page
 * 
 * This page handles the OIDC redirect callback from OneLogin.
 * It shows a loading state while the authentication is being processed,
 * then redirects to the home page once complete.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useAuth } from '../auth';

/** Same-origin path stored by `login()`; anything else falls back to home. */
function getReturnTo(state: unknown): string {
  const returnTo = (state as { returnTo?: unknown } | undefined)?.returnTo;
  if (
    typeof returnTo !== 'string' ||
    !returnTo.startsWith('/') ||
    returnTo.startsWith('//') ||
    returnTo.startsWith('/\\') ||
    returnTo.startsWith('/auth/callback')
  ) {
    return '/';
  }
  return returnTo;
}

export function AuthCallbackPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const oidc = useOidcAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Once authentication is complete (not loading anymore), redirect to home
    if (!isLoading && !oidc.activeNavigator) {
      if (isAuthenticated) {
        navigate(getReturnTo(oidc.user?.state), { replace: true });
      } else if (oidc.error) {
        // Error is shown below, don't navigate
        console.error('Authentication error:', oidc.error);
      } else {
        // No error but not authenticated - might still be processing
        // Give it a moment before redirecting
        const timeout = setTimeout(() => {
          if (!oidc.isLoading && !oidc.isAuthenticated) {
            navigate('/', { replace: true });
          }
        }, 2000);
        return () => clearTimeout(timeout);
      }
    }
  }, [isAuthenticated, isLoading, navigate, oidc.activeNavigator, oidc.error, oidc.isLoading, oidc.isAuthenticated, oidc.user?.state]);

  // Show error if authentication failed
  if (oidc.error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="subtitle1" fontWeight={500} gutterBottom>
            Sign in failed
          </Typography>
          <Typography variant="body2">
            {oidc.error.message}
          </Typography>
        </Alert>
        <Button variant="contained" onClick={() => navigate('/', { replace: true })}>
          Return to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        gap: 2,
      }}
    >
      <CircularProgress size={48} />
      <Typography variant="body1" color="text.secondary">
        Completing sign in...
      </Typography>
    </Box>
  );
}
